import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Card, Badge, Pagination } from 'react-bootstrap';
import {  FaMapMarkerAlt, FaBriefcase, FaGlobe, FaCog, FaCloudUploadAlt, 
         FaMobileAlt, FaChartLine, FaCode, FaLaptopCode, 
         FaDatabase,
         FaIndustry,
         FaRobot,
         FaLeaf,
         FaMountain,
         FaWifi,
         FaShieldAlt,
         FaRocket, } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/careers.css';

const CareerPage = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobDetailsView, setIsJobDetailsView] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    resume: null,
    coverLetter: ''
  });
  const jobsPerPage = 5;

  // Sample job data - in a real application, this would come from your API or database
  useEffect(() => {
   const sampleJobs = [
  // Geology & Geophysics Roles
  {
    id: 1,
    title: "Senior Geologist",
    location: "Denver, CO",
    industry: "geology",
    department: "Earth Sciences",
    type: "Full-time",
    description: "Lead geological surveys and analysis for resource exploration and environmental assessment projects.",
    responsibilities: [
      "Conduct field studies and geological mapping",
      "Analyze rock, soil, and mineral samples",
      "Prepare geological reports and technical documentation",
      "Use GIS software for geological data visualization",
      "Collaborate with geophysicists and environmental scientists"
    ],
    requirements: [
      "Master's degree in Geology or related field",
      "5+ years of experience in geological survey and analysis",
      "Proficiency in GIS software (ArcGIS, QGIS)",
      "Knowledge of geological modeling software",
      "Professional Geologist (PG) license preferred"
    ]
  },
  {
    id: 2,
    title: "Geophysicist",
    location: "Houston, TX",
    industry: "geophysics",
    department: "Earth Sciences",
    type: "Full-time",
    description: "Apply geophysical methods to study subsurface structures and properties for various applications.",
    responsibilities: [
      "Design and conduct geophysical surveys using seismic, gravity, and magnetic methods",
      "Process and interpret geophysical data",
      "Develop 3D subsurface models",
      "Collaborate with geologists on integrated earth science projects",
      "Present findings to clients and stakeholders"
    ],
    requirements: [
      "PhD or Master's in Geophysics or related field",
      "4+ years of experience in geophysical data acquisition and processing",
      "Proficiency in geophysical software (Petrel, SeisWorks, etc.)",
      "Strong mathematical and analytical skills",
      "Experience with seismic interpretation and modeling"
    ]
  },
  {
    id: 3,
    title: "Geoscientist - Environmental",
    location: "Portland, OR",
    industry: "environmental",
    department: "Earth Sciences",
    type: "Full-time",
    description: "Apply geological and geophysical principles to environmental consulting and remediation projects.",
    responsibilities: [
      "Conduct environmental site assessments",
      "Monitor groundwater and soil contamination",
      "Design remediation strategies for contaminated sites",
      "Prepare environmental impact reports",
      "Ensure compliance with environmental regulations"
    ],
    requirements: [
      "Bachelor's or Master's degree in Environmental Science, Geology, or related field",
      "3+ years of environmental consulting experience",
      "Knowledge of environmental regulations (CERCLA, RCRA)",
      "Experience with environmental modeling software",
      "Professional certification preferred (PG, CEG)"
    ]
  },

  // Emerging Technology Roles
  {
    id: 4,
    title: "Blockchain Solutions Developer",
    location: "San Francisco, CA",
    industry: "blockchain",
    department: "Emerging Technologies",
    type: "Full-time",
    description: "Develop and implement blockchain solutions for enterprise applications and decentralized systems.",
    responsibilities: [
      "Design and develop smart contracts using Solidity",
      "Build DApps using Web3 technologies",
      "Implement consensus mechanisms and security protocols",
      "Integrate blockchain solutions with existing systems",
      "Research and implement new blockchain technologies"
    ],
    requirements: [
      "4+ years of blockchain development experience",
      "Proficiency in Solidity, JavaScript, and Web3.js",
      "Experience with Ethereum, Hyperledger, or similar platforms",
      "Strong understanding of cryptography and distributed systems",
      "Knowledge of smart contract security best practices"
    ]
  },
  {
    id: 5,
    title: "Quantum Computing Research Engineer",
    location: "Boston, MA",
    industry: "quantum",
    department: "Research & Development",
    type: "Full-time",
    description: "Develop quantum algorithms and applications for solving complex computational problems.",
    responsibilities: [
      "Design and implement quantum algorithms",
      "Work with quantum hardware platforms (IBM Q, Google Quantum AI)",
      "Develop quantum software using Qiskit, Cirq, or similar frameworks",
      "Research quantum error correction and noise mitigation",
      "Collaborate with academic institutions and industry partners"
    ],
    requirements: [
      "PhD in Physics, Computer Science, or related field",
      "3+ years of experience in quantum computing research",
      "Proficiency in quantum programming languages and frameworks",
      "Strong background in quantum mechanics and linear algebra",
      "Experience with quantum simulation and optimization"
    ]
  },
  {
    id: 6,
    title: "Space Technology Engineer",
    location: "Los Angeles, CA",
    industry: "aerospace",
    department: "Space Systems",
    type: "Full-time",
    description: "Design and develop spacecraft systems and satellite technologies for commercial and government applications.",
    responsibilities: [
      "Design spacecraft subsystems (power, propulsion, communications)",
      "Conduct thermal and structural analysis of space systems",
      "Develop flight software and ground control systems",
      "Perform system integration and testing",
      "Support launch operations and mission operations"
    ],
    requirements: [
      "Master's degree in Aerospace Engineering or related field",
      "5+ years of spacecraft design and development experience",
      "Knowledge of space environment and mission requirements",
      "Experience with CAD software and simulation tools",
      "Understanding of space regulations and standards"
    ]
  },
  {
    id: 7,
    title: "LLM Developer",
    location: "Seattle, WA",
    industry: "AI/ML",
    department: "Artificial Intelligence",
    type: "Full-time",
    description: "Develop and fine-tune large language models for various business applications and use cases.",
    responsibilities: [
      "Design and train large language models using transformer architectures",
      "Fine-tune pre-trained models for specific domains and tasks",
      "Implement efficient training and inference pipelines",
      "Develop evaluation metrics and benchmarks for model performance",
      "Optimize models for deployment in production environments"
    ],
    requirements: [
      "PhD or Master's in Computer Science, AI, or related field",
      "4+ years of experience in deep learning and NLP",
      "Proficiency in PyTorch, TensorFlow, and Hugging Face transformers",
      "Experience with distributed training and model optimization",
      "Strong understanding of transformer architectures and attention mechanisms"
    ]
  },

  // Core Technology Roles
  {
    id: 8,
    title: "Senior Mobile App Developer",
    location: "Austin, TX",
    industry: "mobile",
    department: "Engineering",
    type: "Full-time",
    description: "Develop cross-platform mobile applications with focus on performance and user experience.",
    responsibilities: [
      "Design and build native and cross-platform mobile applications",
      "Implement responsive UI/UX designs for mobile platforms",
      "Integrate mobile apps with backend APIs and cloud services",
      "Optimize app performance and battery efficiency",
      "Maintain app store compliance and deployment processes"
    ],
    requirements: [
      "5+ years of mobile development experience (iOS/Android)",
      "Proficiency in Swift, Kotlin, React Native, or Flutter",
      "Experience with mobile app architecture patterns (MVP, MVVM)",
      "Knowledge of mobile security best practices",
      "Experience with app store deployment and CI/CD pipelines"
    ]
  },
  {
    id: 9,
    title: "Senior Data Scientist",
    location: "New York, NY",
    industry: "analytics",
    department: "Data Science",
    type: "Full-time",
    description: "Lead data science initiatives to extract insights from complex datasets and build predictive models.",
    responsibilities: [
      "Develop machine learning models for business optimization",
      "Perform statistical analysis and data mining on large datasets",
      "Create data visualizations and interactive dashboards",
      "Design A/B tests and experiments for product features",
      "Mentor junior data scientists and collaborate with cross-functional teams"
    ],
    requirements: [
      "Master's or PhD in Data Science, Statistics, or related field",
      "5+ years of experience in data science and machine learning",
      "Proficiency in Python, R, SQL, and statistical modeling",
      "Experience with big data technologies (Spark, Hadoop)",
      "Strong business acumen and communication skills"
    ]
  },
  {
    id: 10,
    title: "Cloud Solutions Engineer",
    location: "Remote",
    industry: "cloud",
    department: "Infrastructure",
    type: "Full-time",
    description: "Design and implement scalable cloud infrastructure solutions for enterprise applications.",
    responsibilities: [
      "Architect multi-cloud solutions using AWS, Azure, and GCP",
      "Implement infrastructure as code using Terraform and CloudFormation",
      "Design disaster recovery and high availability solutions",
      "Optimize cloud costs and resource utilization",
      "Ensure security compliance and governance in cloud environments"
    ],
    requirements: [
      "4+ years of cloud engineering experience",
      "Professional certifications in AWS, Azure, or GCP",
      "Experience with containerization (Docker, Kubernetes)",
      "Knowledge of monitoring and logging tools (CloudWatch, ELK stack)",
      "Strong scripting skills in Python or PowerShell"
    ]
  },
  {
    id: 11,
    title: "DevOps Engineer",
    location: "Chicago, IL",
    industry: "devops",
    department: "Engineering Operations",
    type: "Full-time",
    description: "Streamline development and deployment processes through automation and CI/CD best practices.",
    responsibilities: [
      "Build and maintain CI/CD pipelines using Jenkins, GitLab, or GitHub Actions",
      "Automate infrastructure provisioning and configuration management",
      "Monitor application performance and system reliability",
      "Implement security scanning and compliance automation",
      "Collaborate with development teams on deployment strategies"
    ],
    requirements: [
      "3+ years of DevOps or Site Reliability Engineering experience",
      "Proficiency in scripting languages (Python, Bash, PowerShell)",
      "Experience with configuration management tools (Ansible, Chef, Puppet)",
      "Knowledge of monitoring tools (Prometheus, Grafana, New Relic)",
      "Understanding of microservices architecture and containerization"
    ]
  },

  // Business & Management Roles
  {
    id: 12,
    title: "Business Analyst - Technology",
    location: "Dallas, TX",
    industry: "business",
    department: "Business Operations",
    type: "Full-time",
    description: "Analyze business requirements and translate them into technical solutions and process improvements.",
    responsibilities: [
      "Gather and document business requirements from stakeholders",
      "Analyze current business processes and identify improvement opportunities",
      "Create functional specifications and user stories for development teams",
      "Facilitate meetings between business and technical teams",
      "Perform user acceptance testing and quality assurance"
    ],
    requirements: [
      "Bachelor's degree in Business Administration, Information Systems, or related field",
      "4+ years of business analysis experience in technology environments",
      "Proficiency in requirements gathering and documentation tools",
      "Knowledge of Agile/Scrum methodologies",
      "Strong analytical and problem-solving skills"
    ]
  },
  {
    id: 13,
    title: "Business Administrator",
    location: "Phoenix, AZ",
    industry: "administration",
    department: "Business Operations",
    type: "Full-time",
    description: "Oversee daily business operations and administrative functions to ensure organizational efficiency.",
    responsibilities: [
      "Manage office operations and administrative processes",
      "Coordinate with HR for employee onboarding and benefits administration",
      "Oversee vendor relationships and contract management",
      "Prepare reports and presentations for executive leadership",
      "Ensure compliance with company policies and procedures"
    ],
    requirements: [
      "Bachelor's degree in Business Administration or related field",
      "3+ years of administrative or operations management experience",
      "Proficiency in Microsoft Office Suite and project management tools",
      "Strong organizational and communication skills",
      "Experience with budget management and financial reporting"
    ]
  },
  {
    id: 14,
    title: "Business Development Consultant",
    location: "Miami, FL",
    industry: "consulting",
    department: "Business Development",
    type: "Full-time",
    description: "Drive business growth through strategic partnerships, client acquisition, and market expansion initiatives.",
    responsibilities: [
      "Identify and develop new business opportunities and partnerships",
      "Conduct market research and competitive analysis",
      "Prepare proposals and presentations for potential clients",
      "Negotiate contracts and partnership agreements",
      "Maintain relationships with key clients and stakeholders"
    ],
    requirements: [
      "MBA or Bachelor's degree in Business, Marketing, or related field",
      "5+ years of business development or sales experience in technology sector",
      "Strong presentation and negotiation skills",
      "Experience with CRM systems and sales analytics tools",
      "Proven track record of meeting sales targets and revenue goals"
    ]
  },

  // Additional Technical Roles
  {
    id: 15,
    title: "Full Stack Developer",
    location: "San Jose, CA",
    industry: "web",
    department: "Engineering",
    type: "Full-time",
    description: "Develop end-to-end web applications using modern technologies and frameworks.",
    responsibilities: [
      "Build responsive web applications using React, Angular, or Vue.js",
      "Develop RESTful APIs and microservices using Node.js or Python",
      "Design and optimize database schemas and queries",
      "Implement authentication and authorization systems",
      "Ensure code quality through testing and code reviews"
    ],
    requirements: [
      "4+ years of full stack development experience",
      "Proficiency in JavaScript, TypeScript, HTML, and CSS",
      "Experience with modern frameworks (React, Angular, Vue.js)",
      "Knowledge of backend technologies (Node.js, Python, Java)",
      "Understanding of database design and optimization"
    ]
  },
  {
    id: 16,
    title: "UI/UX Designer",
    location: "Portland, OR",
    industry: "design",
    department: "Product Design",
    type: "Full-time",
    description: "Create intuitive and engaging user interfaces for web and mobile applications.",
    responsibilities: [
      "Design user interfaces and user experiences for digital products",
      "Create wireframes, prototypes, and design mockups",
      "Conduct user research and usability testing",
      "Collaborate with developers to ensure design implementation",
      "Maintain design systems and style guides"
    ],
    requirements: [
      "Bachelor's degree in Design, HCI, or related field",
      "3+ years of UI/UX design experience",
      "Proficiency in design tools (Figma, Sketch, Adobe Creative Suite)",
      "Understanding of user-centered design principles",
      "Portfolio demonstrating design process and problem-solving skills"
    ]
  },
  {
    id: 17,
    title: "Cybersecurity Specialist",
    location: "Washington, DC",
    industry: "security",
    department: "Information Security",
    type: "Full-time",
    description: "Protect organizational assets through comprehensive security measures and threat assessment.",
    responsibilities: [
      "Monitor and analyze security threats and vulnerabilities",
      "Implement security controls and incident response procedures",
      "Conduct security assessments and penetration testing",
      "Develop security policies and training programs",
      "Investigate security incidents and coordinate response efforts"
    ],
    requirements: [
      "Bachelor's degree in Cybersecurity, Computer Science, or related field",
      "4+ years of cybersecurity experience",
      "Professional certifications (CISSP, CEH, CISM)",
      "Knowledge of security frameworks (NIST, ISO 27001)",
      "Experience with security tools and SIEM platforms"
    ]
  },
  {
    id: 18,
    title: "Product Manager - Technology",
    location: "San Francisco, CA",
    industry: "product",
    department: "Product Management",
    type: "Full-time",
    description: "Lead product strategy and development for technology solutions and platforms.",
    responsibilities: [
      "Define product vision, strategy, and roadmap",
      "Gather and prioritize product requirements from stakeholders",
      "Work with engineering teams to deliver product features",
      "Analyze market trends and competitive landscape",
      "Measure product performance and user satisfaction"
    ],
    requirements: [
      "MBA or Bachelor's degree in Engineering, Business, or related field",
      "5+ years of product management experience in technology companies",
      "Strong analytical and strategic thinking skills",
      "Experience with Agile development methodologies",
      "Excellent communication and leadership abilities"
    ]
  },
  {
    id: 19,
    title: "Machine Learning Engineer",
    location: "Cambridge, MA",
    industry: "AI/ML",
    department: "Artificial Intelligence",
    type: "Full-time",
    description: "Deploy and operationalize machine learning models for production environments.",
    responsibilities: [
      "Build and deploy ML models into production systems",
      "Design MLOps pipelines for model training and deployment",
      "Optimize model performance and scalability",
      "Monitor model performance and implement retraining strategies",
      "Collaborate with data scientists to productionize research models"
    ],
    requirements: [
      "Master's degree in Computer Science, Machine Learning, or related field",
      "4+ years of experience in ML engineering or similar role",
      "Proficiency in Python, Docker, and cloud platforms",
      "Experience with ML frameworks (TensorFlow, PyTorch, scikit-learn)",
      "Knowledge of MLOps tools and practices"
    ]
  },
  {
    id: 20,
    title: "Technical Project Manager",
    location: "Atlanta, GA",
    industry: "project management",
    department: "Engineering Operations",
    type: "Full-time",
    description: "Lead cross-functional technical projects from conception to delivery.",
    responsibilities: [
      "Plan, execute, and deliver complex technical projects",
      "Coordinate with engineering, product, and business teams",
      "Manage project timelines, budgets, and resource allocation",
      "Identify and mitigate project risks and dependencies",
      "Communicate project status to stakeholders and leadership"
    ],
    requirements: [
      "Bachelor's degree in Engineering, Computer Science, or related field",
      "PMP or Agile certification preferred",
      "5+ years of technical project management experience",
      "Experience with project management tools (Jira, Asana, Microsoft Project)",
      "Strong leadership and communication skills"
    ]
  },
{
    id: 21,
    title: "Petroleum Data Analyst",
    location: "Remote",
    industry: "oil_gas",
    department: "Energy Analytics",
    type: "Full-time",
    description: "Analyze petroleum production data and reservoir performance to optimize extraction operations.",
    responsibilities: [
      "Analyze well performance data and production trends",
      "Create predictive models for reservoir behavior and decline curves",
      "Develop dashboards and reports for drilling and completion teams",
      "Monitor real-time drilling operations and provide data-driven recommendations",
      "Collaborate with geologists and engineers on field development strategies"
    ],
    requirements: [
      "Bachelor's degree in Petroleum Engineering, Data Science, or related field",
      "3+ years of experience in oil and gas data analysis",
      "Proficiency in Python, R, and SQL for data analysis",
      "Knowledge of petroleum engineering software (Petrel, Eclipse, CMG)",
      "Understanding of drilling and completion operations"
    ]
  },
  {
    id: 22,
    title: "Renewable Energy Systems Engineer",
    location: "Remote",
    industry: "renewable_energy",
    department: "Clean Energy",
    type: "Full-time",
    description: "Design and optimize renewable energy systems including solar, wind, and energy storage solutions.",
    responsibilities: [
      "Design solar photovoltaic and wind energy systems",
      "Perform energy yield analysis and financial modeling",
      "Optimize energy storage and grid integration solutions",
      "Conduct feasibility studies for renewable energy projects",
      "Ensure compliance with renewable energy standards and regulations"
    ],
    requirements: [
      "Master's degree in Electrical Engineering, Renewable Energy, or related field",
      "4+ years of experience in renewable energy system design",
      "Proficiency in energy modeling software (PVsyst, HOMER, WindPRO)",
      "Knowledge of power electronics and grid integration",
      "Understanding of energy markets and policy frameworks"
    ]
  },
  {
    id: 23,
    title: "Energy Trading Analyst",
    location: "Remote",
    industry: "energy_trading",
    department: "Energy Markets",
    type: "Full-time",
    description: "Analyze energy markets and develop trading strategies for oil, gas, and power commodities.",
    responsibilities: [
      "Monitor global energy markets and price movements",
      "Develop quantitative models for energy price forecasting",
      "Analyze supply and demand fundamentals for energy commodities",
      "Support trading desk with market intelligence and risk analysis",
      "Prepare daily market reports and trading recommendations"
    ],
    requirements: [
      "Bachelor's degree in Finance, Economics, Engineering, or related field",
      "3+ years of experience in energy trading or commodities analysis",
      "Strong analytical skills and proficiency in Excel, Python, or MATLAB",
      "Knowledge of energy markets, derivatives, and risk management",
      "Understanding of oil, gas, and power market fundamentals"
    ]
  },
  {
    id: 24,
    title: "Drilling Operations Engineer",
    location: "Remote",
    industry: "oil_gas",
    department: "Drilling Operations",
    type: "Full-time",
    description: "Provide remote drilling engineering support and optimize drilling operations for maximum efficiency.",
    responsibilities: [
      "Monitor real-time drilling operations and provide technical guidance",
      "Design drilling programs and well trajectories",
      "Analyze drilling performance and identify optimization opportunities",
      "Troubleshoot drilling problems and provide solutions remotely",
      "Ensure compliance with safety and environmental regulations"
    ],
    requirements: [
      "Bachelor's degree in Petroleum Engineering or related field",
      "5+ years of drilling operations experience",
      "Proficiency in drilling software (Landmark, Halliburton, Baker Hughes)",
      "Strong understanding of drilling mechanics and wellbore stability",
      "Experience with directional drilling and horizontal well completions"
    ]
  },
  {
    id: 25,
    title: "Power Systems Analyst",
    location: "Remote",
    industry: "power_utilities",
    department: "Grid Operations",
    type: "Full-time",
    description: "Analyze electrical power systems and support grid operations and planning activities.",
    responsibilities: [
      "Perform power flow analysis and stability studies",
      "Model transmission and distribution systems",
      "Analyze grid integration of renewable energy sources",
      "Support outage management and system restoration planning",
      "Develop load forecasting models and demand response programs"
    ],
    requirements: [
      "Master's degree in Electrical Engineering with power systems focus",
      "3+ years of experience in power systems analysis",
      "Proficiency in power system software (PSS/E, ETAP, PowerWorld)",
      "Knowledge of grid codes and electricity market operations",
      "Understanding of SCADA systems and energy management systems"
    ]
  },

  // Environmental Science & Sustainability Roles
  {
    id: 26,
    title: "Environmental Data Scientist",
    location: "Remote",
    industry: "environmental",
    department: "Environmental Analytics",
    type: "Full-time",
    description: "Apply data science techniques to environmental monitoring and sustainability initiatives.",
    responsibilities: [
      "Analyze environmental monitoring data (air, water, soil quality)",
      "Develop predictive models for environmental impact assessment",
      "Create data visualizations for environmental reporting",
      "Support ESG reporting and sustainability metrics tracking",
      "Collaborate with environmental scientists on research projects"
    ],
    requirements: [
      "Master's degree in Environmental Science, Data Science, or related field",
      "3+ years of experience in environmental data analysis",
      "Proficiency in Python, R, and GIS software",
      "Knowledge of environmental regulations and monitoring standards",
      "Experience with remote sensing and satellite data analysis"
    ]
  },
  {
    id: 27,
    title: "Climate Change Analyst",
    location: "Remote",
    industry: "environmental",
    department: "Climate Science",
    type: "Full-time",
    description: "Analyze climate data and assess climate change impacts on business operations and strategies.",
    responsibilities: [
      "Analyze climate data and develop climate risk assessments",
      "Model climate change impacts on business operations",
      "Support carbon footprint analysis and reduction strategies",
      "Prepare climate disclosure reports and sustainability assessments",
      "Research adaptation and mitigation strategies for climate resilience"
    ],
    requirements: [
      "Master's or PhD in Climate Science, Environmental Science, or related field",
      "4+ years of experience in climate analysis and modeling",
      "Proficiency in climate modeling software and statistical analysis",
      "Knowledge of climate policy frameworks and carbon markets",
      "Experience with TCFD reporting and climate risk assessment"
    ]
  },
  {
    id: 28,
    title: "Environmental Compliance Specialist",
    location: "Remote",
    industry: "environmental",
    department: "Environmental Compliance",
    type: "Full-time",
    description: "Ensure organizational compliance with environmental regulations and sustainability standards.",
    responsibilities: [
      "Monitor compliance with environmental laws and regulations",
      "Conduct environmental audits and assessments",
      "Prepare environmental permits and regulatory submissions",
      "Develop environmental management systems and procedures",
      "Provide training on environmental compliance requirements"
    ],
    requirements: [
      "Bachelor's degree in Environmental Science, Engineering, or related field",
      "4+ years of environmental compliance experience",
      "Knowledge of environmental regulations (NEPA, Clean Air Act, Clean Water Act)",
      "Experience with environmental management systems (ISO 14001)",
      "Strong technical writing and communication skills"
    ]
  },
  {
    id: 29,
    title: "Sustainability Consultant",
    location: "Remote",
    industry: "sustainability",
    department: "Sustainability Services",
    type: "Full-time",
    description: "Provide sustainability consulting services to help organizations achieve their environmental goals.",
    responsibilities: [
      "Conduct sustainability assessments and materiality analyses",
      "Develop sustainability strategies and action plans",
      "Support ESG reporting and stakeholder engagement",
      "Implement circular economy and waste reduction initiatives",
      "Facilitate sustainability training and workshops"
    ],
    requirements: [
      "Master's degree in Sustainability, Environmental Management, or related field",
      "5+ years of sustainability consulting experience",
      "Knowledge of sustainability frameworks (GRI, SASB, UN SDGs)",
      "Experience with life cycle assessment and environmental footprinting",
      "Strong project management and client relationship skills"
    ]
  },
  {
    id: 30,
    title: "Environmental Remediation Engineer",
    location: "Remote",
    industry: "environmental",
    department: "Environmental Engineering",
    type: "Full-time",
    description: "Design and oversee environmental remediation projects for contaminated sites.",
    responsibilities: [
      "Design remediation systems for soil and groundwater cleanup",
      "Conduct feasibility studies for remediation technologies",
      "Monitor remediation progress and optimize treatment systems",
      "Prepare remedial design reports and technical documentation",
      "Coordinate with regulatory agencies and project stakeholders"
    ],
    requirements: [
      "Master's degree in Environmental Engineering or related field",
      "Professional Engineer (PE) license preferred",
      "5+ years of experience in environmental remediation",
      "Knowledge of remediation technologies and treatment processes",
      "Experience with environmental modeling software"
    ]
  },

  // Telecommunications & Technology Roles
  {
    id: 31,
    title: "5G Network Engineer",
    location: "Remote",
    industry: "telecommunications",
    department: "Network Engineering",
    type: "Full-time",
    description: "Design and optimize 5G wireless networks for enhanced mobile connectivity and IoT applications.",
    responsibilities: [
      "Design 5G network architecture and radio access networks",
      "Optimize network performance and coverage planning",
      "Implement network slicing and edge computing solutions",
      "Troubleshoot network issues and performance bottlenecks",
      "Support IoT device integration and massive machine communications"
    ],
    requirements: [
      "Master's degree in Telecommunications, Electrical Engineering, or related field",
      "4+ years of experience in wireless network engineering",
      "Knowledge of 5G standards (3GPP, NR) and network protocols",
      "Experience with network planning tools and RF optimization",
      "Understanding of cloud-native network functions and virtualization"
    ]
  },
  {
    id: 32,
    title: "Technical Data Analyst - Telecom",
    location: "Remote",
    industry: "telecommunications",
    department: "Network Analytics",
    type: "Full-time",
    description: "Analyze telecommunications network data to optimize performance and customer experience.",
    responsibilities: [
      "Analyze network performance data and KPIs",
      "Develop predictive models for network capacity planning",
      "Create automated reports and dashboards for network operations",
      "Perform root cause analysis for network incidents",
      "Support business intelligence initiatives with data insights"
    ],
    requirements: [
      "Bachelor's degree in Data Science, Telecommunications, or related field",
      "3+ years of experience in telecom data analysis",
      "Proficiency in SQL, Python, and data visualization tools",
      "Knowledge of telecom network technologies and protocols",
      "Experience with big data platforms and real-time analytics"
    ]
  },
  {
    id: 33,
    title: "IoT Solutions Architect",
    location: "Remote",
    industry: "iot",
    department: "Internet of Things",
    type: "Full-time",
    description: "Design end-to-end IoT solutions for industrial and consumer applications.",
    responsibilities: [
      "Architect IoT platforms and device connectivity solutions",
      "Design data ingestion and processing pipelines for IoT data",
      "Implement edge computing and real-time analytics solutions",
      "Ensure IoT security and device management best practices",
      "Collaborate with hardware teams on sensor integration"
    ],
    requirements: [
      "Master's degree in Computer Science, Electrical Engineering, or related field",
      "5+ years of experience in IoT solution development",
      "Knowledge of IoT protocols (MQTT, CoAP, LoRaWAN)",
      "Experience with cloud IoT platforms (AWS IoT, Azure IoT, Google Cloud IoT)",
      "Understanding of edge computing and industrial automation"
    ]
  },
  {
    id: 34,
    title: "Digital Transformation Consultant",
    location: "Remote",
    industry: "consulting",
    department: "Digital Strategy",
    type: "Full-time",
    description: "Help organizations navigate digital transformation initiatives and technology adoption.",
    responsibilities: [
      "Assess current technology landscape and digital maturity",
      "Develop digital transformation roadmaps and strategies",
      "Lead change management initiatives for technology adoption",
      "Evaluate and recommend emerging technologies",
      "Support organizational restructuring for digital-first operations"
    ],
    requirements: [
      "MBA or Master's degree in Business, Technology, or related field",
      "6+ years of experience in digital transformation or management consulting",
      "Knowledge of emerging technologies and digital business models",
      "Strong project management and stakeholder engagement skills",
      "Experience with agile transformation and organizational change"
    ]
  },

  // Entry-Level and Internship Opportunities
  {
    id: 35,
    title: "Environmental Science Intern",
    location: "Remote",
    industry: "environmental",
    department: "Environmental Science",
    type: "Internship",
    description: "Gain hands-on experience in environmental science research and data analysis.",
    responsibilities: [
      "Assist with environmental data collection and analysis",
      "Support research projects on environmental monitoring",
      "Prepare technical reports and research documentation",
      "Participate in environmental field studies and surveys",
      "Learn environmental software tools and analytical techniques"
    ],
    requirements: [
      "Currently pursuing Bachelor's or Master's degree in Environmental Science",
      "Strong academic performance in environmental science courses",
      "Basic knowledge of statistics and data analysis",
      "Proficiency in Microsoft Office and willingness to learn new software",
      "Strong written and verbal communication skills"
    ]
  },
  {
    id: 36,
    title: "Junior Technical Data Analyst",
    location: "Remote",
    industry: "analytics",
    department: "Data Analytics",
    type: "Full-time",
    description: "Support technical data analysis projects and develop analytical skills in a technology environment.",
    responsibilities: [
      "Collect, clean, and analyze technical datasets",
      "Create basic data visualizations and reports",
      "Support senior analysts with data preparation and modeling",
      "Learn advanced analytics tools and programming languages",
      "Participate in cross-functional projects and meetings"
    ],
    requirements: [
      "Bachelor's degree in Data Science, Engineering, Mathematics, or related field",
      "1-2 years of experience with data analysis or recent graduate",
      "Basic proficiency in SQL and Excel",
      "Willingness to learn Python, R, or other analytical tools",
      "Strong analytical thinking and attention to detail"
    ]
  },
  {
    id: 37,
    title: "Energy Market Research Analyst",
    location: "Remote",
    industry: "energy_research",
    department: "Market Intelligence",
    type: "Full-time",
    description: "Conduct research and analysis on energy markets, trends, and emerging technologies.",
    responsibilities: [
      "Research global energy market trends and regulatory developments",
      "Analyze energy supply and demand dynamics",
      "Prepare market intelligence reports and forecasts",
      "Track emerging energy technologies and their market potential",
      "Support business development with market analysis and insights"
    ],
    requirements: [
      "Master's degree in Energy Economics, Finance, or related field",
      "2+ years of experience in energy market research or analysis",
      "Strong research and analytical skills",
      "Knowledge of energy markets and commodity trading",
      "Excellent written communication and presentation skills"
    ]
  },
  {
    id: 38,
    title: "Remote Sensing Specialist",
    location: "Remote",
    industry: "geospatial",
    department: "Earth Observation",
    type: "Full-time",
    description: "Apply remote sensing technologies for environmental monitoring and geospatial analysis.",
    responsibilities: [
      "Process and analyze satellite imagery and aerial data",
      "Develop applications for environmental monitoring and mapping",
      "Create geospatial databases and web mapping applications",
      "Support climate change research with remote sensing data",
      "Collaborate with GIS analysts and environmental scientists"
    ],
    requirements: [
      "Master's degree in Remote Sensing, Geography, or related field",
      "3+ years of experience in remote sensing and geospatial analysis",
      "Proficiency in remote sensing software (ENVI, ERDAS, Google Earth Engine)",
      "Knowledge of programming languages (Python, R, JavaScript)",
      "Experience with satellite missions and sensor characteristics"
    ]
  },
  {
    id: 39,
    title: "Carbon Accounting Specialist",
    location: "Remote",
    industry: "sustainability",
    department: "Carbon Management",
    type: "Full-time",
    description: "Develop and maintain carbon accounting systems for greenhouse gas emissions tracking and reporting.",
    responsibilities: [
      "Calculate and track Scope 1, 2, and 3 greenhouse gas emissions",
      "Develop carbon accounting methodologies and protocols",
      "Prepare carbon footprint reports and sustainability disclosures",
      "Support carbon offset and neutrality programs",
      "Ensure compliance with carbon reporting standards and regulations"
    ],
    requirements: [
      "Bachelor's degree in Environmental Science, Engineering, or related field",
      "3+ years of experience in carbon accounting or sustainability",
      "Knowledge of GHG Protocol and carbon accounting standards",
      "Experience with carbon management software and databases",
      "Understanding of carbon markets and offset mechanisms"
    ]
  },
  {
    id: 40,
    title: "Environmental Health & Safety Coordinator",
    location: "Remote",
    industry: "environmental",
    department: "Health & Safety",
    type: "Full-time",
    description: "Coordinate environmental health and safety programs to ensure workplace and environmental protection.",
    responsibilities: [
      "Develop and implement EHS policies and procedures",
      "Conduct virtual safety training and awareness programs",
      "Monitor compliance with occupational health and safety regulations",
      "Investigate incidents and develop corrective action plans",
      "Support emergency response planning and preparedness"
    ],
    requirements: [
      "Bachelor's degree in Environmental Health, Safety Engineering, or related field",
      "4+ years of experience in EHS coordination or management",
      "Knowledge of OSHA regulations and environmental compliance",
      "Professional certifications (CSP, CIH, CHMM) preferred",
      "Strong training and communication skills"
    ]
  }
];
    
    setJobs(sampleJobs);
    setFilteredJobs(sampleJobs);
    setTotalPages(Math.ceil(sampleJobs.length / jobsPerPage));
  }, []);

  // Filter jobs based on search criteria
  useEffect(() => {
    let results = jobs;
    
    if (searchTitle.trim() !== "") {
      results = results.filter(job => 
        job.title.toLowerCase().includes(searchTitle.toLowerCase())
      );
    }
    
    if (searchLocation.trim() !== "") {
      results = results.filter(job => 
        job.location.toLowerCase().includes(searchLocation.toLowerCase())
      );
    }
    
    if (selectedIndustry !== "") {
      results = results.filter(job => job.industry === selectedIndustry);
    }
    
    setFilteredJobs(results);
    setTotalPages(Math.ceil(results.length / jobsPerPage));
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTitle, searchLocation, selectedIndustry, jobs]);

  // Get current jobs for pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View job details
  const viewJobDetails = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setSelectedJob(job);
    setIsJobDetailsView(true);
    window.scrollTo(0, 0);
  };

  // Go back to job listings
  const backToJobListings = () => {
    setIsJobDetailsView(false);
    setSelectedJob(null);
  };

  // Handle input change for application form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle file change for resume upload
  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      resume: e.target.files[0]
    });
  };
  
  // Handle application form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Application submitted successfully!");
    setIsJobDetailsView(false);
    setSelectedJob(null);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      resume: null,
      coverLetter: ''
    });
  };

  
  // Marquee component for scrolling job fields
  const JobFieldsMarquee = () => {
   const jobFields = [
  { name: "Software Engineering", icon: <FaLaptopCode /> },
  { name: "Blockchain Technology", icon: <FaDatabase /> },
  { name: "Quantum Computing", icon: <FaCog /> },
  { name: "Energy & Renewables", icon: <FaGlobe /> },
  { name: "Oil & Gas", icon: <FaIndustry /> },
  { name: "Cloud & DevOps", icon: <FaCloudUploadAlt /> },
  { name: "Web Development", icon: <FaCode /> },
  { name: "Mobile Development", icon: <FaMobileAlt /> },
  { name: "Data Science & Analytics", icon: <FaChartLine /> },
  { name: "Artificial Intelligence", icon: <FaRobot /> },
  { name: "Environmental Sciences", icon: <FaLeaf /> },
  { name: "Earth Sciences", icon: <FaMountain /> },
  { name: "Telecommunications", icon: <FaWifi /> },
  { name: "Business & Consulting", icon: <FaBriefcase /> },
  { name: "Cybersecurity", icon: <FaShieldAlt /> },
  { name: "Space Technology", icon: <FaRocket /> },
];

    return (
      <div className="marquee-container">
        <div className="marquee-content">
          {jobFields.concat(jobFields).map((field, index) => (
            <div key={index} className="marquee-item">
              <span className="marquee-icon">{field.icon}</span>
              <span className="marquee-text">{field.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Hero section component
  const HeroSection = () => {
    return (
      <div className="hero-section6">
        <div className="hero-overlay">
          <Container>
            <Row className="align-items-center hero-content">
              <Col md={6}>
                <h1 className="hero-title">Join our talent community</h1>
                <p className="hero-subtitle">
                  We are always looking for talent around the world. Create an account, apply for
                  current openings and stay up to date on upcoming job opportunities.
                </p>
                <Form className="hero-form">
                  <Form.Group className="mb-3">
                    <Form.Control type="email" placeholder="Email address..." />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="sign-up-btn">
                    Sign up now
                  </Button>
                </Form>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    );
  };

  // Job listings section component
  const JobListings = () => {
    return (
      <section className="job-listings-section">
        <Container>
          <h2 className="text-center mb-5">Job Openings</h2>
          <p className="text-center mb-4">
            Begin your job search now and discover exciting career opportunities at our company
          </p>
          
          <Row className="mb-4">
            <Col md={5}>
              <Form.Group>
                <div className="search-input">
                  <FaBriefcase className="search-icon" />
                  <Form.Control 
                    type="text" 
                    placeholder="Search by keyword" 
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <div className="search-input">
                  <FaMapMarkerAlt className="search-icon" />
                  <Form.Control 
                    type="text" 
                    placeholder="Search by location" 
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button variant="primary" className="w-100 search-btn">
                Search openings
              </Button>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col>
              <div className="industry-filters">
                <Button 
                  variant={selectedIndustry === "" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("")}
                >
                  All
                </Button>
                <Button 
                  variant={selectedIndustry === "technology" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("technology")}
                >
                  <FaLaptopCode className="me-1" /> Technology
                </Button>
                <Button 
                  variant={selectedIndustry === "blockchain" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("blockchain")}
                >
                  <FaDatabase className="me-1" /> Blockchain
                </Button>
                <Button 
                  variant={selectedIndustry === "quantum" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("quantum")}
                >
                  <FaCog className="me-1" /> Quantum
                </Button>
                <Button 
                  variant={selectedIndustry === "energy" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("energy")}
                >
                  <FaGlobe className="me-1" /> Energy
                </Button>
                 <Button 
                  variant={selectedIndustry === "Ai_ml" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("Ai_ml")}
                >
                  <FaGlobe className="me-1" /> AI & ML
                </Button>
                <Button 
                  variant={selectedIndustry === "oil_gas" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("oil")}
                >
                  <FaGlobe className="me-1" /> Oil Industry
                </Button>
                <Button 
                  variant={selectedIndustry === "cloud" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("cloud")}
                >
                  <FaCloudUploadAlt className="me-1" /> Cloud
                </Button>
                <Button 
                  variant={selectedIndustry === "web" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("web")}
                >
                  <FaCode className="me-1" /> Web Dev
                </Button>
                <Button 
                  variant={selectedIndustry === "mobile" ? "primary" : "outline-primary"}
                  className="me-2 mb-2"
                  onClick={() => setSelectedIndustry("mobile")}
                >
                  <FaMobileAlt className="me-1" /> Mobile
                </Button>
              </div>
            </Col>
          </Row>
          
          {filteredJobs.length === 0 ? (
            <div className="text-center py-5">
              <h4>No jobs match your search criteria</h4>
              <p>Try adjusting your search terms or filters</p>
            </div>
          ) : (
            <>
              <Row>
                {currentJobs.map(job => (
                  <Col md={12} key={job.id} className="mb-4">
                    <Card className="job-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <Card.Title>{job.title}</Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">
                              <FaMapMarkerAlt className="me-1" /> {job.location}
                            </Card.Subtitle>
                            <div className="mb-3">
                              <Badge bg="primary" className="me-2">{job.type}</Badge>
                              <Badge bg="secondary" className="me-2">{job.department}</Badge>
                            </div>
                            <Card.Text className="job-description">{job.description}</Card.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Button variant="outline-primary" onClick={() => viewJobDetails(job.id)}>
                              View Details
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  />
                  
                  {[...Array(totalPages).keys()].map(number => (
                    <Pagination.Item 
                      key={number + 1}
                      active={number + 1 === currentPage}
                      onClick={() => paginate(number + 1)}
                    >
                      {number + 1}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next 
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </>
          )}
        </Container>
      </section>
    );
  };

  // Job details component
  const JobDetails = () => {
    if (!selectedJob) return null;
    
    return (
      <div className="job-details-page">
        <Container className="py-5">
          <Button onClick={backToJobListings} variant="outline-primary" className="mb-4">
            &larr; Back to Jobs
          </Button>
          
          <Card className="mb-5">
            <Card.Body>
              <h1>{selectedJob.title}</h1>
              <div className="mb-4">
                <Badge bg="primary" className="me-2">{selectedJob.type}</Badge>
                <Badge bg="secondary" className="me-2">{selectedJob.department}</Badge>
                <span className="text-muted">
                  <FaMapMarkerAlt className="me-1" /> {selectedJob.location}
                </span>
              </div>
              
              <div className="job-description mb-4">
                <h5>Job Description</h5>
                <p>{selectedJob.description}</p>
              </div>
              
              <div className="job-responsibilities mb-4">
                <h5>Key Responsibilities</h5>
                <ul>
                  {selectedJob.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </div>
              
              <div className="job-requirements mb-4">
                <h5>Requirements</h5>
                <ul>
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
              
              <div className="text-center mt-5">
                <h3>Apply for this position</h3>
                <p>Submit your application below</p>
              </div>
              
              <Form onSubmit={handleSubmit} className="application-form mt-4">
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="fullName" 
                        value={formData.fullName} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Resume</Form.Label>
                      <Form.Control 
                        type="file" 
                        name="resume" 
                        onChange={handleFileChange} 
                        required 
                      />
                      <Form.Text className="text-muted">
                        Upload your resume (PDF, DOC, DOCX)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-4">
                  <Form.Label>Cover Letter</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={5} 
                    name="coverLetter" 
                    value={formData.coverLetter} 
                    onChange={handleInputChange} 
                    placeholder="Tell us why you're a good fit for this position"
                  />
                </Form.Group>
                
                <div className="text-center">
                  <Button variant="primary" type="submit" size="lg">
                    Submit Application
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  };


  return (
    <div className="career-page">
    
      {!isJobDetailsView ? (
        <>
          <HeroSection />
          <JobFieldsMarquee />
          <JobListings />
        </>
      ) : (
        <JobDetails />
      )}
     
    </div>
  );
};

export default CareerPage;
